/* eslint-disable @typescript-eslint/no-explicit-any */
import ContributionSkeleton from "@/components/Common/Skeletons/ContributionSkeleton";
import { isEnrolledStatus } from "@/types/employeeState";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getAllBeneficiariesData } from "../../../services/beneficiaries";
import { RootState } from "../../../store";
import { BeneficiaryType } from "../../../types/beneficiaries";
import {
  EmployeeDecodedData,
  EmployeeTokenData,
} from "../../../types/contributions";
import { getDecodedTokenSafe } from "../../../utils/tokenUtils";
import Tooltip from "../../Common/Tooltip";

const INITIAL_EMPLOYER_MATCH: EmployeeDecodedData = {
  UserAccountID: "",
  EmployeeID: 0,
  FirstName: "",
  LastName: "",
  EmployerMaxMatch: 0,
  EmployerMaxAnnualContribution: 0,
  EmployeeSalary: 0,
};

const ContributionSettings: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user);
  const [loading, setLoading] = useState<boolean>(true);
  const [beneficiaries, setBeneficiaries] = useState<[]>([]);
  const [employeeDeductions, setEmployeeDeductions] = useState<number>(0);
  const [employerDeductions, setEmployerDeductions] = useState<number>(0);
  const [totalPayCheckDeductions, setTotalPayCheckDeductions] =
    useState<number>(0);
  const [employeeInformation, setEmployeeInformation] =
    useState<EmployeeDecodedData>(INITIAL_EMPLOYER_MATCH);

  /*   const { employerContributionInPercent, employerContributionInDollars } =
    useSelector((state: RootState) => state.wizard.contributionDeductions); */

  const handleFetchGoals = async () => {
    setLoading(true);
    try {
      if (user.id) {
        await fetchEmployerContribution();

        const response = await getAllBeneficiariesData(
          user.employeeID,
          user.id,
        );

        const filteredBeneficiaries = response.data.filter((benefiicary: any) =>
          isEnrolledStatus(benefiicary.minorEnrolledStatus),
        );

        if (filteredBeneficiaries.length > 0) {
          setBeneficiaries(filteredBeneficiaries);

          const totalEmployeeDeductions = filteredBeneficiaries.reduce(
            (sum: number, b: BeneficiaryType) =>
              sum + b.employeeMinorContributionInDollar,
            0,
          );

          const totalEmployerDeductions = filteredBeneficiaries.reduce(
            (sum: number, b: BeneficiaryType) =>
              sum + b.employerMinorContributionInDollar,
            0,
          );

          setEmployeeDeductions(Math.trunc(totalEmployeeDeductions));
          setEmployerDeductions(Math.trunc(totalEmployerDeductions));
          setTotalPayCheckDeductions(
            Math.trunc(totalEmployeeDeductions) +
              Math.trunc(totalEmployerDeductions),
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployerContribution = async () => {
    const decodedToken = getDecodedTokenSafe<EmployeeTokenData>(() => {});

    if (decodedToken) {
      setEmployeeInformation({
        UserAccountID: decodedToken.UserAccountID,
        EmployeeID: decodedToken.EmployeeID,
        FirstName: decodedToken.FirstName,
        LastName: decodedToken.LastName,
        EmployerMaxMatch: decodedToken.EmployerMaxContribution,
        EmployeeSalary: decodedToken.EmployeeSalary,
        EmployerMaxAnnualContribution:
          decodedToken.EmployeeSalary *
          (decodedToken.EmployerMaxContribution / 100),
      });
    }
  };

  useEffect(() => {
    handleFetchGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <ContributionSkeleton />
  ) : (
    <>
      {employeeInformation.EmployerMaxMatch.toString() !== "0" && (
        <>
          <div className="p-[32px] flex justify-between items-center">
            <div>
              <p className="text-lg font-normal text-[#8B8B8B]">
                {t("savings_settings.contributions.max_annual_contribution")}
              </p>
              <div className="flex space-x-1 items-center">
                <p>{employeeInformation.EmployerMaxMatch}%</p>
                <Tooltip
                  text={t(
                    "onboarding.contributions.form_tooltips.annual_max_contribution",
                  )}
                />
              </div>
            </div>
          </div>
          <hr />
        </>
      )}
      {beneficiaries.map((beneficiary: BeneficiaryType) => (
        <div key={beneficiary.minorAccountID}>
          <div className="p-[32px]">
            <div className="space-y-2">
              <p className="font-medium text-[18px] py-2">
                {beneficiary.firstName} {beneficiary.lastName}
              </p>
              <div className="space-y-1 text-black">
                <div className="flex justify-between">
                  <span className="py-2">
                    {t("savings_settings.contributions.your_contribution")}
                  </span>
                  <span className="py-2">
                    ${Math.trunc(beneficiary.employeeMinorContributionInDollar)}
                  </span>
                </div>
                {employeeInformation.EmployerMaxMatch.toString() !== "0" && (
                  <div className="flex justify-between">
                    <span className="py-2">
                      {t("savings_settings.contributions.employer_matching")}
                    </span>
                    <span className="text-black py-2">
                      + $
                      {Math.trunc(
                        beneficiary.employerMinorContributionInDollar,
                      )}
                    </span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="py-2">
                    {t("savings_settings.contributions.total_contribution")}
                  </span>
                  <span className="py-2">
                    $
                    {Math.trunc(beneficiary.employeeMinorContributionInDollar) +
                      Math.trunc(beneficiary.employerMinorContributionInDollar)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <hr />
        </div>
      ))}

      <div className="bg-[#F9F9F9]">
        <div className=" p-[32px] space-y-1 text-black border-t">
          <div className="flex justify-between">
            <div className="flex space-x-1 items-center">
              <p>{t("savings_settings.contributions.monthly_deduction")}</p>
              <Tooltip
                text={t(
                  "onboarding.contributions.form_tooltips.monthly_deductions",
                )}
              />
            </div>
            <span className="py-2">${employeeDeductions}</span>
          </div>
          {employeeInformation.EmployerMaxMatch.toString() !== "0" && (
            <div className="flex justify-between">
              <div className="flex space-x-1 items-center">
                <p>{t("savings_settings.contributions.employer_matching")}</p>
                <Tooltip
                  text={t(
                    "onboarding.contributions.form_tooltips.employer_matching",
                  )}
                />
              </div>
              <span className="text-black py-2">+ ${employerDeductions}</span>
            </div>
          )}

          <div className="flex justify-between font-medium">
            <span className="py-2">
              {t("savings_settings.contributions.total_monthly_savings")}
            </span>
            <span className="py-2">${totalPayCheckDeductions}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContributionSettings;
